import React from 'react';
import { BookOpen, X } from 'lucide-react';

interface SeriesItem {
  series_name: string;
  recap_count: number;
  series_thumbnail?: string | null;
}

interface SeriesListProps {
  series: SeriesItem[];
  loading: boolean;
  selectedLetter?: string;
  selectedGenre?: string;
  total?: number;
  hasMore?: boolean;
  onSeriesClick: (seriesName: string) => void;
  onLoadMore?: () => void;
  onClose: () => void;
}

export const SeriesList: React.FC<SeriesListProps> = ({
  series,
  loading,
  selectedLetter,
  selectedGenre,
  total,
  hasMore,
  onSeriesClick,
  onLoadMore,
  onClose
}) => {
  // Only show loading screen if we have no series yet (initial load)
  // If we have series and are loading, show them with a loading indicator
  if (loading && series.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-center text-gray-600 dark:text-gray-400">Loading series...</div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {selectedGenre
              ? `Series with genre "${selectedGenre}"`
              : `Series starting with "${selectedLetter}"`
            }
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            ({total !== undefined ? `${series.length} of ${total}` : series.length} {series.length === 1 ? 'series' : 'series'})
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
          title="Close series list"
        >
          <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        </button>
      </div>

      {series.length === 0 ? (
        <div className="text-center text-gray-600 dark:text-gray-400 py-8">
          {selectedGenre
            ? `No series found with genre "${selectedGenre}"`
            : `No series found starting with "${selectedLetter}"`
          }
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {series.map((item, index) => {
              // Construct thumbnail URL if available
              // series_thumbnail format: "A/filename.webp" (unified structure, no data_source prefix)
              // Live server structure: /media/thumbnails/{first_letter}/{filename}
              // For backward compatibility, strip data_source prefix if present (animeplanet/, mangadex/, manhuaus/)
              let thumbnailUrl = null;
              if (item.series_thumbnail) {
                let thumbnailPath = item.series_thumbnail;
                // Strip data_source prefix if present (for backward compatibility with old entries)
                if (thumbnailPath.includes('/')) {
                  const parts = thumbnailPath.split('/');
                  if (parts.length > 1 && ['animeplanet', 'mangadex', 'manhuaus'].includes(parts[0].toLowerCase())) {
                    thumbnailPath = parts.slice(1).join('/'); // Remove data_source prefix
                  }
                }
                thumbnailUrl = `/media/thumbnails/${thumbnailPath}`;
              }

              // Debug logging for first few items
              if (index < 3) {
                console.log('Series thumbnail debug:', {
                  series_name: item.series_name,
                  series_thumbnail: item.series_thumbnail,
                  thumbnailUrl: thumbnailUrl,
                  hasThumbnail: !!item.series_thumbnail
                });
              }

              return (
                <button
                  key={item.series_name}
                  data-series-index={index}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Series button clicked:', item.series_name);
                    onSeriesClick(item.series_name);
                  }}
                  className="text-center p-3 bg-gray-50 dark:bg-gray-700 hover:bg-purple-50 dark:hover:bg-purple-900/20 border border-gray-200 dark:border-gray-600 rounded-lg transition-colors duration-200 group flex flex-col items-center gap-2"
                >
                  {thumbnailUrl ? (
                    <img
                      src={thumbnailUrl}
                      alt={item.series_name}
                      className="w-full aspect-[2/3] object-cover rounded"
                      onError={(e) => {
                        // Log error for debugging
                        console.error('Thumbnail failed to load:', thumbnailUrl, 'for series:', item.series_name);
                        // Hide image on error
                        e.currentTarget.style.display = 'none';
                      }}
                      onLoad={() => {
                        // Log success for debugging
                        if (item.series_name.toLowerCase().includes('dont-tell-me-you-think-cultivating')) {
                          console.log('Thumbnail loaded successfully:', thumbnailUrl);
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full aspect-[2/3] bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                    </div>
                  )}
                  <div className="w-full">
                    <div className="font-medium text-sm text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors break-words line-clamp-2">
                      {item.series_name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {item.recap_count} {item.recap_count === 1 ? 'recap' : 'recaps'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Load More Button */}
          {hasMore && onLoadMore && (
            <div className="mt-6 flex justify-center" data-load-more-button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onLoadMore();
                }}
                disabled={loading}
                className="px-6 py-3 bg-purple-600 dark:bg-purple-500 text-white rounded-lg hover:bg-purple-700 dark:hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
          {/* Show loading indicator below existing series when loading more */}
          {loading && series.length > 0 && (
            <div className="mt-4 text-center text-gray-600 dark:text-gray-400">
              Loading more series...
            </div>
          )}
        </>
      )}
    </div>
  );
};

